import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiHelpers } from '@/lib/api';
import { formatDate, getProjectUrl } from '@/lib/utils';
import type { BlogPostStatus } from '@/types';
import {
  Plus,
  Search,
  FileText,
  Loader2,
  Trash2,
  Edit,
  ArrowLeft,
  Sparkles,
  Lightbulb,
  ExternalLink,
  Send,
  Archive,
  FolderTree,
} from 'lucide-react';
import { AIBlogGenerator } from '@/components/AIBlogGenerator';
import BlogCategoryManagerDialog from '@/components/blog/BlogCategoryManagerDialog';
import { qk } from '@/lib/queryKeys';

export default function Blog() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BlogPostStatus | ''>('');
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showCategories, setShowCategories] = useState(false);

  // Fetch project data for URL construction
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => apiHelpers.getProject(projectId!),
    enabled: Boolean(projectId),
  });

  const project = projectData?.data?.data;

  const { data: postsData, isLoading } = useQuery({
    queryKey: [...qk.blogPosts(projectId), search, statusFilter],
    queryFn: () =>
      apiHelpers.getBlogPosts(projectId!, {
        search: search || undefined,
        status: statusFilter || undefined,
        limit: 50,
      }),
    enabled: Boolean(projectId),
  });

  const { data: categoriesData } = useQuery({
    queryKey: qk.blogCategories(projectId),
    queryFn: () => apiHelpers.getBlogCategories(projectId!),
    enabled: Boolean(projectId),
  });

  const posts = postsData?.data?.data || [];
  const categories = categoriesData?.data?.data || [];

  const deleteMutation = useMutation({
    mutationFn: (postId: string) => apiHelpers.deleteBlogPost(projectId!, postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.blogPosts(projectId) });
      toast.success('Post deleted');
      setShowDeleteConfirm(null);
    },
    onError: () => {
      toast.error('Failed to delete post');
    },
  });

  // Inline status transitions so admins never need to open the editor just to
  // publish or archive. Publish is intentionally one-way per product rule
  // ("once published it cannot be draft again") — the button only shows for
  // drafts. Archive is available for any non-archived post.
  const statusMutation = useMutation({
    mutationFn: ({ postId, status }: { postId: string; status: BlogPostStatus }) =>
      apiHelpers.updateBlogPost(projectId!, postId, { status }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: qk.blogPosts(projectId) });
      toast.success(vars.status === 'published' ? 'Post published' : 'Post archived');
    },
    onError: () => {
      toast.error('Failed to change status');
    },
  });

  const getStatusColor = (status: BlogPostStatus) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'archived':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryName = (categoryId?: string) => {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/projects/${projectId}`}
          className="hover:bg-muted rounded-lg p-2 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Blog Posts</h1>
          <p className="text-muted-foreground">Create and manage blog content</p>
        </div>
        <Link
          to={`/projects/${projectId}/blog/topic-map`}
          className="hover:bg-muted inline-flex items-center gap-2 rounded-lg border px-4 py-2 font-medium transition-colors"
        >
          <FolderTree className="h-4 w-4" />
          Topic Map
        </Link>
        <Link
          to={`/projects/${projectId}/blog/suggestions`}
          className="inline-flex items-center gap-2 rounded-lg border border-amber-400/60 bg-amber-50 px-4 py-2 font-medium text-amber-700 transition-colors hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300"
        >
          <Lightbulb className="h-4 w-4" />
          Trend Suggestions
        </Link>
        <button
          onClick={() => setShowCategories(true)}
          className="hover:bg-muted inline-flex items-center gap-2 rounded-lg border px-4 py-2 font-medium transition-colors"
        >
          <FolderTree className="h-4 w-4" />
          Categories
        </button>
        <button
          onClick={() => setShowAIGenerator(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2 font-medium text-white transition-all hover:from-purple-700 hover:to-pink-700"
        >
          <Sparkles className="h-4 w-4" />
          Generate with AI
        </button>
        <button
          onClick={() => navigate(`/projects/${projectId}/blog/new`)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Post
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-background focus:ring-primary w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as BlogPostStatus | '')}
          className="bg-background focus:ring-primary rounded-lg border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="scheduled">Scheduled</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Posts Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-card rounded-xl border py-12 text-center">
          <FileText className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
          <h3 className="mb-2 font-semibold">No blog posts yet</h3>
          <p className="text-muted-foreground mb-4">
            {search || statusFilter
              ? 'Try a different search or filter'
              : 'Create your first blog post'}
          </p>
          {!search && !statusFilter && (
            <button
              onClick={() => navigate(`/projects/${projectId}/blog/new`)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Post
            </button>
          )}
        </div>
      ) : (
        <div className="bg-card overflow-hidden rounded-xl border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Title
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Status
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Category
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Author
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-left text-sm font-medium">
                    Date
                  </th>
                  <th className="text-muted-foreground px-6 py-3 text-right text-sm font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-muted/30">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium">{post.title}</p>
                        <p className="text-muted-foreground text-sm">/{post.slug}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${getStatusColor(post.status)}`}
                      >
                        {post.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{getCategoryName(post.categoryId)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{post.authorName || 'Unknown'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-muted-foreground text-sm">
                        {post.publishedAt
                          ? formatDate(post.publishedAt)
                          : formatDate(post.createdAt)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {post.status === 'published' && (
                          <button
                            onClick={() => {
                              // Construct the public blog URL using project domain
                              const baseUrl = project ? getProjectUrl(project) : null;
                              if (baseUrl) {
                                const blogUrl = `${baseUrl}/blog/${post.slug}`;
                                window.open(blogUrl, '_blank', 'noopener,noreferrer');
                              } else {
                                toast.error('No domain configured for this project');
                              }
                            }}
                            className="hover:bg-muted rounded p-1 transition-colors hover:text-blue-600"
                            title="View published post"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        )}
                        {post.status === 'draft' && (
                          <button
                            onClick={() =>
                              statusMutation.mutate({ postId: post.id, status: 'published' })
                            }
                            disabled={statusMutation.isPending}
                            className="hover:bg-muted rounded p-1 transition-colors hover:text-green-600 disabled:opacity-50"
                            title="Publish"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        )}
                        {post.status !== 'archived' && (
                          <button
                            onClick={() => {
                              if (
                                confirm(`Archive "${post.title}"? It won't appear on the site.`)
                              ) {
                                statusMutation.mutate({ postId: post.id, status: 'archived' });
                              }
                            }}
                            disabled={statusMutation.isPending}
                            className="hover:bg-muted rounded p-1 transition-colors hover:text-amber-600 disabled:opacity-50"
                            title="Archive"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/projects/${projectId}/blog/${post.id}/edit`)}
                          className="hover:bg-muted rounded p-1 transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(post.id)}
                          className="hover:bg-muted rounded p-1 transition-colors hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Generator Modal — on accept, open the full-page editor prefilled. */}
      {showAIGenerator && (
        <AIBlogGenerator
          projectIndustry={project?.industry}
          onGenerated={(post, selectedImage) => {
            setShowAIGenerator(false);
            navigate(`/projects/${projectId}/blog/new`, {
              state: { aiGeneratedData: { post, image: selectedImage } },
            });
          }}
          onClose={() => setShowAIGenerator(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/50"
            onClick={() => setShowDeleteConfirm(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card w-full max-w-md rounded-xl border p-6 shadow-lg">
              <h3 className="mb-2 text-lg font-semibold">Delete Post?</h3>
              <p className="text-muted-foreground mb-6 text-sm">
                This action cannot be undone. The post will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate(showDeleteConfirm)}
                  disabled={deleteMutation.isPending}
                  className="flex flex-1 items-center justify-center rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {projectId && (
        <BlogCategoryManagerDialog
          projectId={projectId}
          open={showCategories}
          onOpenChange={setShowCategories}
        />
      )}
    </div>
  );
}
